import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UploadZone = ({ onFileUpload, disabled, disabledMessage }) => {
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (disabled) {
      setError(disabledMessage || t('pdfUpload.dropzone.errors.disabled'));
      return;
    }

    setError(null);

    if (rejectedFiles?.length > 0) {
      const rejection = rejectedFiles?.[0];
      if (rejection?.errors?.[0]?.code === 'file-too-large') {
        setError(t('pdfUpload.dropzone.errors.fileTooLarge'));
      } else if (rejection?.errors?.[0]?.code === 'file-invalid-type') {
        setError(t('pdfUpload.dropzone.errors.invalidType'));
      } else {
        setError(t('pdfUpload.dropzone.errors.uploadFailed'));
      }
      return;
    }

    if (acceptedFiles?.length > 0) {
      onFileUpload(acceptedFiles?.[0]);
    }
  }, [onFileUpload, disabled, disabledMessage, t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    multiple: false,
    disabled
  });

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-lg p-8 md:p-12 text-center
          transition-all duration-250
          ${disabled 
            ? 'border-border bg-muted/30 cursor-not-allowed opacity-60' 
            : `cursor-pointer ${
              isDragActive
                ? 'border-accent bg-accent/5 scale-[1.02]'
                : 'border-border bg-card hover:border-accent/50 hover:bg-accent/5'
            }`
          }
        `}
      >
        <input {...getInputProps()} disabled={disabled} />
        
        <div className="flex flex-col items-center gap-4">
          <div className={`
            w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center
            transition-colors duration-250
            ${isDragActive ? 'bg-accent/20' : 'bg-muted'}
          `}>
            <Icon
              name={disabled ? 'AlertCircle' : (isDragActive ? 'Upload' : 'FileUp')}
              size={32}
              className={`md:w-10 md:h-10 ${isDragActive ? 'text-accent' : 'text-muted-foreground'}`}
            />
          </div>

          <div className="space-y-2">
            <h3 className="text-lg md:text-xl font-semibold text-foreground">
              {disabled 
                ? t('pdfUpload.dropzone.uploadUnavailable')
                : (isDragActive 
                  ? t('pdfUpload.dropzone.titleActive')
                  : t('pdfUpload.dropzone.title')
                )
              }
            </h3>
            <p className="text-sm md:text-base text-muted-foreground">
              {disabled 
                ? (disabledMessage || t('pdfUpload.dropzone.disabled.default'))
                : t('pdfUpload.dropzone.subtitle')
              }
            </p>
          </div>

          {!disabled && (
            <Button
              variant="outline"
              size="lg"
              iconName="FolderOpen"
              iconPosition="left"
              className="mt-2"
            >
              {t('pdfUpload.dropzone.browseFiles')}
            </Button>
          )}

          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground mt-4">
            <div className="flex items-center gap-1">
              <Icon name="FileText" size={14} />
              <span>{t('pdfUpload.dropzone.pdfOnly')}</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon name="HardDrive" size={14} />
              <span>{t('pdfUpload.dropzone.maxSize')}</span>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive rounded-lg p-4 flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-destructive">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-destructive hover:text-destructive/80 transition-colors"
          >
            <Icon name="X" size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default UploadZone;